import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface PhantomJobContextType {
  hasActivePhantomJobs: boolean;
  isLoading: boolean;
  refreshStatus: () => void;
}

const PhantomJobContext = createContext<PhantomJobContextType | undefined>(undefined);

export const usePhantomJobStatus = () => {
  const context = useContext(PhantomJobContext);
  if (context === undefined) {
    throw new Error('usePhantomJobStatus must be used within a PhantomJobProvider');
  }
  return context;
};

interface PhantomJobProviderProps {
  children: React.ReactNode;
}

export const PhantomJobProvider: React.FC<PhantomJobProviderProps> = ({ children }) => {
  const [hasActivePhantomJobs, setHasActivePhantomJobs] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkPhantomJobStatus = async () => {
    try {
      const response = await apiService.getPhantomJobStatus();
      setHasActivePhantomJobs(response.hasActivePhantomJobs);
    } catch (error: any) {
      // Silently handle connection errors when server isn't running
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('ERR_CONNECTION_REFUSED') ||
          error?.name === 'TypeError') {
        // Server is not available - this is expected in development when backend isn't running
        setHasActivePhantomJobs(false);
        return;
      }
      // Only log unexpected errors
      console.error('Error checking phantom job status:', error);
      setHasActivePhantomJobs(false);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = () => {
    checkPhantomJobStatus();
  };

  useEffect(() => {
    checkPhantomJobStatus();
    
    // Check every 5 seconds for more responsive detection
    const interval = setInterval(checkPhantomJobStatus, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const value = {
    hasActivePhantomJobs,
    isLoading,
    refreshStatus
  };

  return (
    <PhantomJobContext.Provider value={value}>
      {children}
    </PhantomJobContext.Provider>
  );
};
